import React, { useState, useEffect } from "react";
import { LinkCard } from "../../stories/cards/LinkCard";
import Boots from "../../assets/images/Vector.svg";
import ContinueJourney from "./ContinueJourney";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useApis from "../../services/useAPI";
import { summaryUrl, bookingUrl } from "../../utils/constants";
import { getTestTypeValueFromUrl } from "../../utils";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { InfoCircle } from "react-bootstrap-icons";
import NotFound from "../../components/NotFound";
import { Spinner } from "react-bootstrap";
import ConfirmTestType from "./ConfirmTestType";

/**
 * PhlebotomyDashboard component
 * Displays the user's phlebotomy test journey and handles navigation between various states of the booking process.
 *
 * @returns {JSX.Element} The rendered dashboard component, with either a booking option or a continue journey option.
 */

const PhlebotomyDashboard = props => {
  const { test_type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const [untestable, setUntestable] = useState();
  const [resume, setResume] = useState();
  const [allowBooking, setAllowBooking] = useState();
  const [rejected, setRejected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState("");

  const { callApi, resultGet, serverError } = useApis();

  useEffect(() => {
    localStorage.setItem("verifyCard", false);
    localStorage.setItem("isLoggedIn", true);
  }, []);
  // Fetches data for pending journey if any
  const fetchSummary = () => {
    callApi(null, summaryUrl, "GET");
  };

  // Sets the booking rejection state.
  const isBookingRejected = () => {
    setRejected(true);
  };

  // Fetches booking details if the user is authenticated.
  const fetchBookings = () => {
    setIsLoading(true);
    callApi(null, bookingUrl, "GET");
  };

  // Updates state based on the API response.
  useEffect(() => {
    if (resultGet?.bookings) {
      setAllowBooking(resultGet?.allow_booking);
      setResume(resultGet.bookings ? resultGet.bookings[0] : null);
      setIsLoading(false);
    } else if (resultGet?.latest_test) {
      setUntestable(resultGet.latest_test?.lab_failed);
      setIsLoading(false);
    }
  }, [resultGet, allowBooking]);

  // Fetches bookings when the access token or rejection state changes.
  useEffect(() => {
    fetchBookings();
  }, [rejected]);

  // Fetches summary if the user has an incomplete journey.
  useEffect(() => {
    if (
      resume?.registration_stage === "assessment" ||
      resume?.registration_stage === "edit-details"
    ) {
      fetchSummary();
    }
  }, [resume]);

  // Determine if redirect is needed based on the booking or test status
  useEffect(() => {
    if (
      (auth?.isHcpLive && resume?.registration_stage === "assessment") ||
      resume?.registration_stage === "edit-details"
    ) {
      if (
        !allowBooking &&
        !untestable &&
        resume.booking_status !== "completed"
      ) {
        // Instead of navigating immediately, set the redirect path
        const path = `/bloods/boots/success-page/${getTestTypeValueFromUrl(test_type)}/${resume?.id
          }`;
        setRedirectPath(path);
        setShouldRedirect(true);
      } else if (!allowBooking && auth?.isHcpLive) {
        auth?.showToast(
          "You have a test currently in process",
          "Once you receive the results for your on going (In-store Wellness Testing Service) test, you'll be able to book a new one.",
          "../assets/mhc.png",
          <InfoCircle />
        );
        setRedirectPath("/tests");
        setShouldRedirect(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume, allowBooking, untestable, rejected]);

  // Perform the actual navigation after determining the redirect path
  useEffect(() => {
    if (shouldRedirect && redirectPath) {
      navigate(redirectPath);
    }
  }, [shouldRedirect, redirectPath, navigate]);

  // Checks the URL for any rejection status query parameter.
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get("rejected") === "true") {
      setRejected(true);
    }
  }, [location.search]);

  // Show a loading indicator while data is being fetched
  if (isLoading && !serverError) {
    return (
      <div className="text-center">
        <Spinner animation="border" role='output' className="my-custom-spinner"/>
      </div>
    );
  }

  if (serverError) {
    return <p className="text-muted">{serverError}</p>;
  }

  // If redirect is needed, return an empty fragment to prevent UI flash
  if (shouldRedirect) {
    return null;
  }

  return (
    <div className="row justify-content-center dashboard-page">
      {auth?.isHcpLive ? (
        <div className="col-md-12">
          <div className="dashboard">
            {allowBooking ? (
              <ConfirmTestType />
            ) : (
              <ContinueJourney
                untestable={untestable}
                resume={resume}
                isBookingRejected={isBookingRejected}
                test_type={
                  getTestTypeValueFromUrl(test_type) !== "unknown"
                    ? getTestTypeValueFromUrl(test_type)
                    : resume?.test_type
                }
              />
            )}
          </div>
        </div>
      ) : (
        <NotFound />
      )}
    </div>
  );
};

export default PhlebotomyDashboard;